import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  ListingCardComponent,
  ListingSkeletonComponent,
  PaginatorComponent,
} from '@out-web/components';
import { IListing, IListingsResponse, ListingsService } from '@out-web/core';

@Component({
  selector: 'out-models',
  imports: [
    ListingCardComponent,
    CommonModule,
    ListingSkeletonComponent,
    PaginatorComponent,
    RouterLink,
  ],
  templateUrl: './models.component.html',
  styleUrl: './models.component.css',
})
export class ModelsComponent implements OnInit {
  modelName!: string | null;
  formattedModelName = '';
  rawFormat: string | null = 'model-s.webp';
  placeholderSkeletons = Array(6).fill(0);
  listings: IListing[] = [];

  @Input() paginateOnType?: string;
  @Input() pageResults = 0;
  @Input() skipResults = 0;

  currentPage = 0;
  isLoading = false;
  noListingsMessage = '';

  constructor(
    private meta: Meta,
    private titleService: Title,
    private route: ActivatedRoute,
    private listingsService: ListingsService
  ) {}

  ngOnInit(): void {
    this.subscribeToRouteParams();
    this.setMetaData();
  }

  private subscribeToRouteParams(): void {
    this.route.paramMap.subscribe((params) => {
      this.modelName = params.get('model');
      this.formattedModelName = this.formatModelName(this.modelName);
      this.rawFormat = this.modelName;
      this.resetListings();
      this.loadListings();
      this.fetchFilters();
      this.fetchObservableStates();
    });
  }

  private fetchObservableStates() {
    this.listingsService.listingsState$.subscribe((state) => {
      this.pageResults = state.totalListings;
    });
  }

  private formatModelName(name: string | null): string {
    if (!name) return '';
    return name === 'cybertruck'
      ? name.toUpperCase()
      : name.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private resetListings(): void {
    this.skipResults = 0;
    this.listings = [];
  }

  private setMetaData(): void {
    const title = `Used Tesla ${this.formattedModelName} For Sale | Sell Your Tesla | Only Used Tesla`;
    const description = `Only Used Tesla ${this.formattedModelName} For Sale | A Place Exclusively Dedicated To Buying And Selling Used Teslas`;

    this.titleService.setTitle(title);
    this.meta.addTags([
      {
        name: 'description',
        content: description,
        vmid: 'description',
      },
      {
        property: 'og:description',
        content: description,
        hid: 'og:description',
      },
      {
        name: 'keywords',
        content:
          'Used Tesla, Buy Tesla Canada, Tesla Model 3, Tesla Model S, Used Electric Cars, Affordable Tesla, Certified Tesla',
      },
      {
        name: 'author',
        content: 'Only Used Tesla',
      },
    ]);
  }

  private loadListings(): void {
    this.isLoading = true;
    this.listingsService
      .fetchListings(
        { listingType: '!Sold', 'vehicle.model': this.formattedModelName },
        'category',
        true
      )
      .subscribe({
        next: this.onListingsLoadSuccess.bind(this),
        error: this.onListingsLoadError.bind(this),
      });
  }

  private fetchFilters(): void {
    try {
      this.listingsService
        .fetchFilters(
          { listingType: '!Sold', 'vehicle.model': this.formattedModelName },
          true
        )
        .subscribe();
    } catch (error) {
      console.error('Fetching filters error:', error);
    }
  }

  private onListingsLoadSuccess(data: IListingsResponse): void {
    this.listings = data.listings;
    this.isLoading = false;
    this.noListingsMessage =
      this.listings.length === 0
        ? `No ${this.formattedModelName} listings found.`
        : '';
  }

  private onListingsLoadError(error: HttpErrorResponse): void {
    this.isLoading = false;
    console.error('Error loading listings:', error);
  }

  onPaginationChange(skipNumber: number): void {
    this.listingsService.updateSkipListings(skipNumber);
    this.skipResults = skipNumber;
    // Load listings with updated skipResults
    this.loadListings();
  }
}
